import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Select, 
  Button, 
  Table, 
  Space, 
  Modal, 
  message,
  Divider,
  Row,
  Col,
  Popconfirm,
  App
} from 'antd';
import { 
  FileSearchOutlined
} from '@ant-design/icons';
import { Plus, Trash2, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AxiosCaller from '../../../../authenticatie/AxiosCaller';
import { openNotificationSuccess, openNotificationError } from '../../../../Components/Notification/Notification';

const { Title, Text } = Typography;
const { Content } = Layout;

const MenuOverzichtScreen = () => {
  const { get, del } = AxiosCaller();
  const navigate = useNavigate();


  // States
  const [selectedDishType, setSelectedDishType] = useState(null);
  const [selectedKlasType, setSelectedKlasType] = useState(null);
  const [voorgerechten, setVoorgerechten] = useState([]);
  const [hoofdgerechten, setHoofdgerechten] = useState([]);
  const [desserts, setDesserts] = useState([]);
  const [updatedRecepten, setUpdateRecepten] = useState(false);
  const [klasTypes, setKlasTypes] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);

  // Dish Types
  const dishTypes = [
    { value: 'voorgerecht', label: 'Voorgerecht' },
    { value: 'hoofdgerecht', label: 'Hoofdgerecht' },
    { value: 'nagerecht', label: 'Nagerecht' }
  ];

   // Fetch KlasTypes
   const fetchKlasTypes = async () => {
    try {
      const response = await get('/klasType');
      if (Array.isArray(response.data)) {
        setKlasTypes(response.data.map(type => ({
          value: type._id,
          label: type.naam
        })));
      }
    } catch (error) {
      console.error('Error fetching klastypes:', error);
      openNotificationError("Fout bij ophalen van klastypes", error)
    }
  };

  // Fetch Menu Items
  const fetchMenuItems = async (type, klasType) => {
    try {
      const endpoint = {
        'voorgerecht': '/recept/voorgerechten',
        'hoofdgerecht': '/recept/hoofdgerechten',
        'nagerecht': '/recept/nagerechten'
      }[type];

      const url = klasType ? `${endpoint}/${klasType}` : endpoint;
      const response = await get(url);

      const processedData = response.data.map(item => ({
        ...item,
        key: item._id
      }));

      switch(type) {
        case 'voorgerecht': 
          setVoorgerechten(processedData);
          break;
        case 'hoofdgerecht': 
          setHoofdgerechten(processedData);
          break;
        case 'nagerecht': 
          setDesserts(processedData);
          break;
      }
    } catch (error) {
      openNotificationError(`Fout bij ophalen van ${type}en`, error)
      console.error(`Error fetching ${type}:`, error);
    }
  };

  // Effect Hooks
  useEffect(() => {
    fetchKlasTypes();
  }, []);

  useEffect(() => {
    if (selectedDishType) {
      fetchMenuItems(selectedDishType.value, selectedKlasType?.value);
      // Clear selected menu and ingredients when dish type or klas type changes
      setSelectedMenu(null);
    }
  }, [selectedDishType, selectedKlasType, updatedRecepten]);

  // Remove Menu Handler
  const handleRemove = async (menu) => {
    try {
      await del(`/recept/${menu._id}`);
      openNotificationSuccess("recept succesvol verwijderd")
      setUpdateRecepten(!updatedRecepten);
      setSelectedMenu(null);
    } catch (error) {
      console.error(error)
      openNotificationError("Fout bij verwijderen van recept", error)
    }
  };
  // Edit Menu Handler
  const handleEdit = (menu) => {
    navigate('/Admin/configuratie/MakenAanpassenMenu', { state: menu });
  };

  // Menu Columns
  const menuColumns = [
    {
      title: 'Naam',
      dataIndex: 'naam',
      key: 'naam',
      render: (text) => <strong style={{ color: '#333' }}>{text}</strong>
    },
    {
      title: 'Acties',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Space>
          <Button 
            size="small"
            type="text"
            icon={<Pencil/>} 
            onClick={() => handleEdit(record)}
          />
          <Popconfirm 
            title="Weet je het zeker?" 
            onConfirm={() => handleRemove(record)}
            okText="Ja"
            cancelText="Nee"
          >
            <Button 
              size="small"
              type="text"
              danger
              icon={<Trash2/>} 
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Ingredients Columns
  const ingredientsColumns = [
    {
      title: 'Ingrediënt',
      dataIndex: 'ingrediënt',
      key: 'ingrediënt',
      render: (text) => <strong style={{ color: '#333' }}>{text}</strong>
    },
    {
      title: 'Aantal',
      dataIndex: 'aantal',
      key: 'aantal',
      width: 150
    }
  ];

  // Get Ingredients for Selected Menu
  const getIngrediënten = () => {
    if (!selectedMenu?.producten) return [];

    return selectedMenu.producten.map((item, index) => ({
      key: index,
      ingrediënt: item.product.naam,
      aantal: `${item.aantal * selectedMenu.aantalPersonen} ${item.product.eenheid.naam}`
    }));
  };

 // Get Menu Data Source
 const getMenuDataSource = () => {
  if (!selectedDishType) return [];

  // Select the correct menu array based on dish type
  const menuData = 
    selectedDishType.value === 'voorgerecht' ? voorgerechten :
    selectedDishType.value === 'hoofdgerecht' ? hoofdgerechten :
    selectedDishType.value === 'nagerecht' ? desserts : [];

  // If a klas type is selected, filter the menu data
  if (selectedKlasType) {
    const filteredData = menuData.filter(menu => {
      // Check if klasType is an object or a string
      const menuKlasTypeId = 
        typeof menu.klasType === 'object' 
          ? menu.klasType._id 
          : menu.klasType;
      
      return menuKlasTypeId === selectedKlasType.value;
    });

    return filteredData;
  }

  return menuData;
};

  return (

      <Content className='Screen-container'>
        <div className='space-between'>
          <h1 className='title'>Menu</h1>
          <Button onClick={() => navigate('/Admin/configuratie/MakenAanpassenMenu')} type="primary" className='button'>
            <Plus /> Menu toevoegen
          </Button>
        </div>
        <div 
          
          style={{ 
          backgroundColor: 'white', 
          borderRadius: '8px', 
          padding: '24px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        }}>
          {/* Header Section */}
          <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
            <Col>
            
            </Col>
            <Col>

            </Col>
          </Row>

          {/* Filters Section */}
          <Row gutter={16} style={{ marginBottom: '24px' }}>
            <Col span={12}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                Gerecht Type
              </Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Selecteer Gerecht Type"
                options={dishTypes}
                value={selectedDishType?.value}
                onChange={(value) => {
                  setSelectedDishType({ value });
                  setSelectedMenu(null); // Clear selected menu when dish type changes
                }}
              />
            </Col>
            <Col span={12}>
              <Text strong style={{ display: 'block', marginBottom: '8px' }}>
                Klas Type
              </Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Selecteer Klas Type"
                options={klasTypes}
                value={selectedKlasType?.value}
                onChange={(value) => {
                  setSelectedKlasType({ value });
                  setSelectedMenu(null); // Clear selected menu when klas type changes
                }}
              />
            </Col>
          </Row>

          <Divider style={{ margin: '24px 0' }} />

          {/* Tables Section */}
          <Row gutter={24}>
            <Col span={12}>
              <Title level={4} style={{ marginBottom: '16px' }}>
                <FileSearchOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Menu's
              </Title>
              <Table 
                scroll={{ y: 0.3 * window.innerHeight }}
                columns={menuColumns}
                dataSource={getMenuDataSource()}
                pagination={false}
                onRow={(record) => ({
                  onClick: () => setSelectedMenu(record),
                  style: { 
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                  }
                })}
                locale={{
                  emptyText: (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <FileSearchOutlined style={{ fontSize: 64, color: '#bfbfbf' }} />
                      <p style={{ color: '#8c8c8c', marginTop: '16px' }}>
                        {selectedDishType 
                          ? 'Geen menu\'s gevonden' 
                          : 'Selecteer een gerecht type'}
                      </p>
                    </div>
                  )
                }}
              />
            </Col>
            <Col span={12}>
              <Title level={4} style={{ marginBottom: '16px' }}>
                <FileSearchOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Ingrediënten {selectedMenu ? `(${selectedMenu.aantalPersonen} personen)` : ''}
              </Title>
              <Table 
                scroll={{ y: 0.3 * window.innerHeight }}
                columns={ingredientsColumns}
                dataSource={getIngrediënten()}
                pagination={false}
                locale={{
                  emptyText: (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <FileSearchOutlined style={{ fontSize: 64, color: '#bfbfbf' }} />
                      <p style={{ color: '#8c8c8c', marginTop: '16px' }}>
                        Selecteer een menu om ingrediënten te bekijken
                      </p>
                    </div>
                  )
                }}
              />
            </Col>
          </Row>
        </div>
      </Content>

  );
};

export default MenuOverzichtScreen;